import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Calendar, 
  Download, 
  Filter,
  Clock,
  AlertTriangle,
  CheckCircle,
  Calculator,
  FileText,
  Eye
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const FinancialReportsPage = () => {
  const { company } = useAuth();
  const [cashRegisters, setCashRegisters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    end: new Date().toISOString().split('T')[0] // Today
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [showRegisterDetails, setShowRegisterDetails] = useState(false);

  useEffect(() => {
    if (company?.id) {
      loadCashRegisters();
    }
  }, [company, dateRange]);

  const loadCashRegisters = async () => {
    try {
      setLoading(true);
      const data = await dataHelpers.getCashRegistersHistory(company.id, {
        startDate: `${dateRange.start}T00:00:00`,
        endDate: `${dateRange.end}T23:59:59`
      });
      setCashRegisters(data || []);
    } catch (error) {
      console.error('Error loading cash registers:', error);
      toast.error('Erro ao carregar relatórios financeiros');
      setCashRegisters([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const totalRegisters = cashRegisters.length;
    const totalSales = cashRegisters.reduce((sum, reg) => sum + parseFloat(reg.total_sales || 0), 0);
    const totalCashSales = cashRegisters.reduce((sum, reg) => sum + parseFloat(reg.cash_sales || 0), 0);
    const totalCardSales = cashRegisters.reduce((sum, reg) => sum + parseFloat(reg.card_sales || 0), 0);
    const totalPixSales = cashRegisters.reduce((sum, reg) => sum + parseFloat(reg.pix_sales || 0), 0);
    const totalBalanceSales = cashRegisters.reduce((sum, reg) => sum + parseFloat(reg.balance_sales || 0), 0);
    
    const registersWithDifference = cashRegisters.filter(reg => 
      Math.abs(parseFloat(reg.difference || 0)) > 0.01
    );
    
    const totalDifference = cashRegisters.reduce((sum, reg) => sum + parseFloat(reg.difference || 0), 0);

    return {
      totalRegisters,
      totalSales,
      totalCashSales,
      totalCardSales,
      totalPixSales,
      totalBalanceSales,
      registersWithDifference: registersWithDifference.length,
      totalDifference,
      averageSales: totalRegisters > 0 ? totalSales / totalRegisters : 0
    };
  };

  const stats = calculateStats();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleViewRegisterDetails = (register) => {
    setSelectedRegister(register);
    setShowRegisterDetails(true);
  };

  const exportToCSV = () => {
    if (cashRegisters.length === 0) {
      toast.error('Não há dados para exportar');
      return;
    }

    const headers = [
      'Data Abertura',
      'Data Fechamento',
      'Aberto por',
      'Fechado por',
      'Saldo Inicial',
      'Saldo Final Declarado',
      'Saldo Sistema',
      'Diferença',
      'Total Vendas',
      'Vendas Dinheiro',
      'Vendas Cartão',
      'Vendas PIX',
      'Vendas Saldo',
      'Status'
    ];

    const csvContent = [
      headers.join(','),
      ...cashRegisters.map(register => [
        `"${formatDateTime(register.opened_at)}"`,
        register.closed_at ? `"${formatDateTime(register.closed_at)}"` : '"Aberto"',
        `"${register.opened_by_name || '-'}"`,
        `"${register.closed_by_name || '-'}"`,
        register.opening_balance || 0,
        register.closing_balance || 0,
        register.system_balance || 0,
        register.difference || 0,
        register.total_sales || 0,
        register.cash_sales || 0,
        register.card_sales || 0,
        register.pix_sales || 0,
        register.balance_sales || 0,
        `"${register.status === 'open' ? 'Aberto' : 'Fechado'}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio_financeiro_${dateRange.start}_${dateRange.end}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Relatório exportado com sucesso!');
  };

  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Relatórios Financeiros</h1>
            <p className="text-gray-600">
              Análise de caixas e movimentações financeiras
            </p>
          </div>
          <div className="mt-4 sm:mt-0 space-x-2">
            <Button variant="outline" onClick={exportToCSV}>
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Caixas Analisados</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalRegisters}</p>
                <p className="text-sm text-gray-500">no período</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Faturamento Total</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(stats.totalSales)}</p>
                <p className="text-sm text-gray-500">vendas realizadas</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <Calculator className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Média por Caixa</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(stats.averageSales)}</p>
                <p className="text-sm text-gray-500">faturamento médio</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-full ${stats.registersWithDifference > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                {stats.registersWithDifference > 0 ? (
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                )}
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Divergências</p>
                <p className={`text-2xl font-semibold ${stats.registersWithDifference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {stats.registersWithDifference}
                </p>
                <p className="text-sm text-gray-500">caixas com diferença</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Payment Methods Breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Vendas em Dinheiro</p>
              <p className="text-lg font-semibold text-green-600">{formatCurrency(stats.totalCashSales)}</p>
              <p className="text-xs text-gray-500">
                {stats.totalSales > 0 ? ((stats.totalCashSales / stats.totalSales) * 100).toFixed(1) : 0}% do total
              </p>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Vendas no Cartão</p>
              <p className="text-lg font-semibold text-blue-600">{formatCurrency(stats.totalCardSales)}</p>
              <p className="text-xs text-gray-500">
                {stats.totalSales > 0 ? ((stats.totalCardSales / stats.totalSales) * 100).toFixed(1) : 0}% do total
              </p>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Vendas PIX</p>
              <p className="text-lg font-semibold text-purple-600">{formatCurrency(stats.totalPixSales)}</p>
              <p className="text-xs text-gray-500">
                {stats.totalSales > 0 ? ((stats.totalPixSales / stats.totalSales) * 100).toFixed(1) : 0}% do total
              </p>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Vendas com Saldo</p>
              <p className="text-lg font-semibold text-orange-600">{formatCurrency(stats.totalBalanceSales)}</p>
              <p className="text-xs text-gray-500">
                {stats.totalSales > 0 ? ((stats.totalBalanceSales / stats.totalSales) * 100).toFixed(1) : 0}% do total
              </p>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
              <div className="flex space-x-2">
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-auto"
                />
                <span className="self-center text-gray-500">até</span>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-auto"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Cash Registers List */}
        <Card>
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Histórico de Caixas</h3>
          </div>
          <div className="overflow-hidden">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Operador
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Faturamento
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Diferença
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 md:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cashRegisters.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">
                        Nenhum caixa encontrado no período selecionado
                      </p>
                    </td>
                  </tr>
                ) : (
                  cashRegisters.map((register) => (
                    <tr key={register.id} className="hover:bg-gray-50">
                      <td className="px-3 md:px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {formatDate(register.opened_at)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {register.status === 'open' ? 'Em andamento' : `Fechado ${formatDate(register.closed_at)}`}
                        </div>
                      </td>

                      <td className="px-3 md:px-6 py-4 hidden md:table-cell">
                        <div className="text-sm text-gray-900">
                          {register.opened_by_name || 'N/A'}
                        </div>
                        {register.closed_by_name && register.status === 'closed' && (
                          <div className="text-xs text-gray-500">
                            Fechado por: {register.closed_by_name}
                          </div>
                        )}
                      </td>

                      <td className="px-3 md:px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(register.total_sales)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Dinheiro: {formatCurrency(register.cash_sales)}
                        </div>
                      </td>

                      <td className="px-3 md:px-6 py-4 hidden lg:table-cell">
                        {register.status === 'closed' ? (
                          <div className={`text-sm font-medium ${
                            Math.abs(parseFloat(register.difference || 0)) < 0.01 
                              ? 'text-green-600' 
                              : 'text-red-600'
                          }`}>
                            {formatCurrency(register.difference)}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">-</span>
                        )}
                      </td>

                      <td className="px-3 md:px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          register.status === 'open' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {register.status === 'open' ? (
                            <>
                              <Clock className="w-3 h-3 mr-1" />
                              Aberto
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Fechado
                            </>
                          )}
                        </span>
                      </td>

                      <td className="px-3 md:px-6 py-4 text-center">
                        <button 
                          onClick={() => handleViewRegisterDetails(register)}
                          title="Ver detalhes"
                          className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-200"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Register Details Modal */}
      {showRegisterDetails && selectedRegister && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-96 overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-medium text-gray-900">Detalhes do Caixa</h3>
              <button
                onClick={() => setShowRegisterDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Data de Abertura</p>
                  <p className="text-gray-900">{formatDateTime(selectedRegister.opened_at)}</p>
                </div>
                {selectedRegister.closed_at && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Data de Fechamento</p>
                    <p className="text-gray-900">{formatDateTime(selectedRegister.closed_at)}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-gray-600">Status</p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    selectedRegister.status === 'open' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedRegister.status === 'open' ? 'Aberto' : 'Fechado'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Saldo Inicial</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(selectedRegister.opening_balance)}
                  </p>
                </div>
                {selectedRegister.closing_balance !== null && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Saldo Final Declarado</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(selectedRegister.closing_balance)}
                    </p>
                  </div>
                )}
                {selectedRegister.system_balance !== null && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Saldo do Sistema</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(selectedRegister.system_balance)}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Vendas Dinheiro</p>
                  <p className="text-lg font-semibold text-green-600">
                    {formatCurrency(selectedRegister.cash_sales)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Vendas Cartão</p>
                  <p className="text-lg font-semibold text-blue-600">
                    {formatCurrency(selectedRegister.card_sales)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Vendas PIX</p>
                  <p className="text-lg font-semibold text-purple-600">
                    {formatCurrency(selectedRegister.pix_sales)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Vendas Saldo</p>
                  <p className="text-lg font-semibold text-orange-600">
                    {formatCurrency(selectedRegister.balance_sales)}
                  </p>
                </div>
              </div>

              {selectedRegister.difference !== null && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-600 mb-2">Diferença</p>
                  <p className={`text-xl font-bold ${
                    Math.abs(parseFloat(selectedRegister.difference || 0)) < 0.01 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {formatCurrency(selectedRegister.difference)}
                  </p>
                  {Math.abs(parseFloat(selectedRegister.difference || 0)) >= 0.01 && (
                    <p className="text-xs text-red-600 mt-1">
                      Há divergência entre o valor declarado e o calculado pelo sistema
                    </p>
                  )}
                </div>
              )}

              {selectedRegister.notes && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Observações</p>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-900">{selectedRegister.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FinancialReportsPage;
